import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { ThemeProvider, createTheme } from "@mui/material";
import api from "../../config/URL";
import { exportVisibleColumnsToCSV } from "../../components/common/ExportCsv";

const StudentCreditNoteHistory = ({ selectedCenter }) => {
  const [filters, setFilters] = useState({
    centerId: selectedCenter,
    name: "",
    creditNote: "",
  });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [isClearFilterClicked, setIsClearFilterClicked] = useState(false);

  /* -------------------- COLUMNS -------------------- */
  const columns = useMemo(
    () => [
      {
        accessorFn: (row, index) => index + 1,
        header: "S.NO",
        enableSorting: true,
        enableHiding: true,
        size: 40,
        Cell: ({ cell }) => (
          <span style={{ textAlign: "center" }}>{cell.getValue()}</span>
        ),
      },
      // {
      //   accessorKey: "id",
      //   header: "",
      //   enableHiding: true,
      //   enableSorting:false,
      //   size: 20,
      //   Cell: ({ row }) => {
      //     const isFinalStatus = ["CANCEL", "CLASS_ASSIGNED"].includes(
      //       row.original.status
      //     );
      //     return isFinalStatus ? (
      //       <span
      //         className={`badge bg-${
      //           row.original.status === "CLASS_ASSIGNED" ? "success" : "danger"
      //         } fw-light`}
      //       >
      //         {row.original.status === "CLASS_ASSIGNED" ? "Assigned" : "Cancel"}
      //       </span>
      //     ) : (
      //       <button className="btn btn-sm px-1 text-white" style={{background:"#eb862a"}}>Forfeit</button>
      //     );
      //   },
      // },
      {
        accessorKey: "name",
        enableHiding: true,
        header: "Name",
      },
      {
        accessorKey: "creditNote",
        enableHiding: true,
        header: "Credit Note",
      },
      {
        accessorKey: "description",
        header: "Description",
        size: 100,
        Cell: ({ cell }) => {
          const fullText = cell.getValue();
          const shortText =
            fullText.split(" ").slice(0, 10).join(" ") +
            (fullText.split(" ").length > 10 ? "..." : "");
          return <span title={fullText}>{shortText}</span>;
        },
      },
      {
        accessorKey: "creditAmount",
        header: "Credit Amount",
        enableHiding: true,
        size: 40,
      },
      {
        accessorKey: "balanceAmount",
        header: "Balance Amount",
        enableHiding: true,
        size: 40,
      },
      {
        accessorKey: "usedAmount",
        header: "Used Amount",
        enableHiding: true,
        size: 40,
      },
      { accessorKey: "createdBy", header: "Created By" },
      {
        accessorKey: "createdAt",
        header: "Created On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      },
      {
        accessorKey: "updatedAt",
        header: "Updated On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10) || "",
      },
      {
        accessorKey: "updatedBy",
        header: "Updated By",
        Cell: ({ cell }) => cell.getValue() || "",
      },
    ],
    []
  );

  /* -------------------- API -------------------- */
  const fetchApiData = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      filters.centerId = selectedCenter;

      for (let key in filters) {
        if (filters[key]) {
          queryParams.append(key, filters[key]);
        }
      }
      let response;
      response = await api.get(
        `/getCreditNoteHistoryByCenterId?${queryParams.toString()}`
      );
      // if (selectedCenter === 0 || selectedCenter === "0") {
      //   response = await api.get(`/getCreditNoteHistoryByCenterId`);
      // } else {
      //   response = await api.get(
      //     `/getCreditNoteHistoryByCenterId?${queryParams.toString()}`
      //   );
      // }
      setData(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
      setIsClearFilterClicked(false);
    }
  };

  useEffect(() => {
    fetchApiData();
  }, [filters, selectedCenter]);

  /* -------------------- THEME -------------------- */
  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454 !important",
            backgroundColor: "#e6edf7 !important",
            fontWeight: "400 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            "&.Mui-disabled .MuiSwitch-track": {
              backgroundColor: "#f5e1d0",
              opacity: 1,
            },
            "&.Mui-disabled .MuiSwitch-thumb": {
              color: "#eb862a",
            },
          },
          track: {
            backgroundColor: "#e0e0e0",
          },
          thumb: {
            color: "#eb862a",
          },
          switchBase: {
            "&.Mui-checked": {
              color: "#eb862a",
            },
            "&.Mui-checked + .MuiSwitch-track": {
              backgroundColor: "#eb862a",
            },
          },
        },
      },
    },
  });

  /* -------------------- FILTERS -------------------- */
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({ ...prevFilters, [name]: value }));
  };

  const clearFilter = () => {
    setFilters({
      centerId: selectedCenter,
      name: "",
      creditNote: "",
    });
    setIsClearFilterClicked(true);
  };

  /* -------------------- MRT INSTANCE -------------------- */
  const table = useMaterialReactTable({
    columns,
    data,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    initialState: {
      columnVisibility: {
        createdBy: false,
        createdAt: false,
        updatedBy: false,
        updatedAt: false,
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <button
        className="btn btn-sm btn-border"
        onClick={() =>
          exportVisibleColumnsToCSV(table, data, "StudentCreditNoteHistory.csv")
        }
      >
        Export CSV
      </button>
    ),
  });

  return (
    <div className="container-fluid px-2 my-4 center">
      <ol
        className="breadcrumb my-3"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          &nbsp;Invoice and Payment
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          &nbsp;Credit Note History
        </li>
      </ol>
      <div className="card">
        <div
          className="mb-3 d-flex justify-content-between align-items-center p-1"
          style={{ background: "#f5f7f9" }}
        >
          <span className="text-muted">
            This database shows the list of{" "}
            <strong style={{ color: "#287f71" }}>Credit Note History</strong>
          </span>
        </div>

        <div className="mb-3 d-flex justify-content-between">
          <div className="individual_fliters d-lg-flex ">
            <div className="form-group mb-0 ms-2 mb-1">
              <input type="hidden" name="centerId" value={filters.centerId} />
            </div>
            <div className="form-group mb-0 ms-2 mb-1">
              <input
                type="text"
                name="name"
                value={filters.name}
                onChange={handleFilterChange}
                className="form-control form-control-sm center_list"
                style={{ width: "160px" }}
                placeholder="Name"
                autoComplete="off"
              />
            </div>
            <div className="form-group mb-0 ms-2 mb-1">
              <input
                type="text"
                name="creditNote"
                value={filters.creditNote}
                onChange={handleFilterChange}
                className="form-control form-control-sm center_list"
                style={{ width: "160px" }}
                placeholder="Credit Note"
                autoComplete="off"
              />
            </div>
            <div className="form-group mb-2 ms-2">
              <button
                type="button"
                onClick={clearFilter}
                className="btn btn-sm btn-border"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <ThemeProvider theme={theme}>
            <MaterialReactTable table={table} />
          </ThemeProvider>
        )}
      </div>
    </div>
  );
};

export default StudentCreditNoteHistory;
